import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  X,
  Check,
  MoreHorizontal,
  Smile,
  Loader2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { AppState } from "@/store";
import {
  getRepliesByComment,
  createComment,
  reactComment,
  removeReactComment,
  getCommentReactions,
  ReactionUser,
} from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import { usePosthog } from "@/hooks/usePosthog";
import { formatTimestamp } from "@/utils/datetime";
import { MentionRenderer } from "./MentionRenderer";
import { ReportDialog, ReportTargetType } from "./ReportDialog";
import { useTranslation } from "react-i18next";
import { CONSTRUCTION_EMOJIS, COMMON_EMOJIS, COMMENT_REACTIONS } from "@/constants";
import { Flag } from "lucide-react";
import { useSuspensionCheck } from "@/hooks/useSuspensionCheck";

interface Comment {
  id: string;
  author: {
    id?: string;
    name: string;
    avatar?: string;
    title: string;
    type?: string;
    organizationId?: string;
  };
  content: string;
  timestamp: Date;
  isEdited?: boolean;
  editedAt?: Date;
  likes: Array<{ userId: string; userName: string; emoji: string }>;
  replies: Array<{
    id: string;
    author: {
      id?: string;
      name: string;
      avatar?: string;
      title: string;
      type?: string;
      organizationId?: string;
    };
    content: string;
    timestamp: Date;
    isEdited?: boolean;
    editedAt?: Date;
    likes: Array<{ userId: string; userName: string; emoji: string }>;
  }>;
  replies_count?: number;
}

interface CommentSectionProps {
  postId: string;
  comments: Comment[];
  postAuthor?: { id?: string; name: string; title: string };
  onComment?: (postId: string, comment: string, mentionedUserIds: string[]) => void;
  onEditComment?: (
    postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => void;
  onDeleteComment?: (postId: string, commentId: string) => void;
  onAddReply?: (
    postId: string,
    commentId: string,
    reply: string,
    mentionedUserIds: string[],
  ) => void;
  onEditReply?: (
    postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => void;
  onDeleteReply?: (postId: string, commentId: string, replyId: string) => void;
  showMoreButton?: React.ReactNode;
  autoExpandCommentId?: string;
  highlightReplyId?: string;
}

export function CommentSection({
  postId,
  comments,
  postAuthor,
  onComment,
  onEditComment,
  onDeleteComment,
  onAddReply,
  onEditReply,
  onDeleteReply,
  showMoreButton,
  autoExpandCommentId,
  highlightReplyId,
}: CommentSectionProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { trackEvent } = usePosthog();
  const { checkAndNotify } = useSuspensionCheck();
  const navigate = useNavigate();
  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;

  // Handle user profile click - navigate to their profile
  const handleUserProfileClick = (userId: string, userType: string, organizationId?: string) => {
    const currentUserType = user?.type;

    // Determine the navigation path based on user types
    if (currentUserType === "job_seeker") {
      if (userType === "job_seeker") {
        // Jobseeker viewing jobseeker - use userId
        navigate(`/jobseeker/users/${userId}`);
      } else if (userType === "employer") {
        // Jobseeker viewing employer - use organization_id if available
        if (organizationId) {
          navigate(`/jobseeker/employers/${organizationId}`);
        }
      } else {
        // If type is not available, default to jobseeker profile
        navigate(`/jobseeker/employers/${userId}`);
      }
    } else if (currentUserType === "employer") {
      if (userType === "employer") {
        // Employer viewing organization - use organization_id if available
        if (organizationId) {
          navigate(`/employer/organizations/${organizationId}`);
        }
      } else if (userType === "job_seeker") {
        // Employer viewing job seeker - use userId
        navigate(`/employer/jobseekers/${userId}`);
      } else {
        // If type is not available, default to organization profile
        navigate(`/employer/organizations/${userId}`);
      }
    }
  };

  const [newComment, setNewComment] = useState("");
  const [commentReactions, setCommentReactions] = useState<
    Record<string, Array<{ userId: string; userName: string; emoji: string }>>
  >({});
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [loadingReplies, setLoadingReplies] = useState<Set<string>>(new Set());
  const [fetchedReplies, setFetchedReplies] = useState<Record<string, any[]>>({});
  const [repliesPage, setRepliesPage] = useState<Record<string, number>>({});
  const [hasMoreReplies, setHasMoreReplies] = useState<Record<string, boolean>>({});
  const [loadingMoreReplies, setLoadingMoreReplies] = useState<Set<string>>(new Set());
  const [showMainCommentEmojiPicker, setShowMainCommentEmojiPicker] = useState(false);
  const [openReplyBoxes, setOpenReplyBoxes] = useState<Set<string>>(new Set()); // Track which comments have reply boxes open
  const replyContentsRef = useRef<Record<string, string>>({}); // Track reply content for each comment (ref to avoid re-renders on every keystroke)
  const mainCommentTextareaRef = useRef<TiptapRef>(null);
  const fetchedReactionsRef = useRef<Set<string>>(new Set());
  const hasHighlightedRef = useRef<string | null>(null);
  const initializedMentionsRef = useRef<Set<string>>(new Set()); // Track which reply boxes have been initialized with mentions

  // Fetch reactions for new comments only (not on every comments change)
  useEffect(() => {
    const fetchReactionsForNewComments = async () => {
      for (const comment of comments) {
        // Skip if already fetched
        if (fetchedReactionsRef.current.has(comment.id)) {
          continue;
        }

        try {
          const result = await getCommentReactions(comment.id, 1, 50);
          const transformedReactions = result.reactions.map((reaction: ReactionUser) => ({
            userId: reaction.user.id,
            userName: reaction.user.full_name,
            emoji: reaction.emoji,
          }));

          // Mark as fetched
          fetchedReactionsRef.current.add(comment.id);

          if (transformedReactions.length > 0) {
            setCommentReactions((prev) => ({
              ...prev,
              [comment.id]: transformedReactions,
            }));
          }
        } catch (error) {
          // Silently fail if no reactions or error
          console.debug(`No reactions for comment ${comment.id}`);
          // Still mark as fetched to avoid retrying
          fetchedReactionsRef.current.add(comment.id);
        }
      }
    };

    if (comments.length > 0) {
      fetchReactionsForNewComments();
    }
  }, [comments.map((c) => c.id).join(",")]);

  // Helper function to get user initials
  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    const nameParts = name.trim().split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };

  // Get translated emoji labels
  const constructionEmojis = CONSTRUCTION_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  const commonEmojis = COMMON_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  const reactions = COMMENT_REACTIONS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  // Helper function to extract mentioned user IDs from TipTap HTML
  const extractMentionedUserIds = (htmlContent: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const mentionSpans = doc.querySelectorAll("span.mention[data-id]");
    return Array.from(mentionSpans)
      .map((span) => span.getAttribute("data-id"))
      .filter((id): id is string => !!id);
  };

  // Helper function to check if TipTap content is truly empty
  const isContentEmpty = (html: string): boolean => {
    if (!html) return true;
    // Remove all HTML tags and check if there's any text content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    return textContent.trim().length === 0;
  };

  // Handler for comment change that clears empty HTML
  const handleCommentChange = (html: string) => {
    if (isContentEmpty(html)) {
      setNewComment("");
      // Clear the editor if it has empty tags
      const editor = mainCommentTextareaRef.current?.editor;
      if (editor && html !== "") {
        editor.commands.setContent("");
      }
    } else {
      setNewComment(html);
    }
  };

  const addEmojiToComment = (emoji: string) => {
    const editor = mainCommentTextareaRef.current?.editor;
    if (editor) {
      editor.commands.insertContent(emoji);
    }
    setShowMainCommentEmojiPicker(false);
  };

  const handleAddComment = () => {
    // Check if user is suspended
    if (checkAndNotify()) {
      // Clear the editor
      const editor = mainCommentTextareaRef.current?.editor;
      if (editor) {
        editor.commands.setContent("");
      }
      setNewComment("");
      return;
    }

    if (newComment.trim()) {
      const mentionedUserIds = extractMentionedUserIds(newComment);

      trackEvent("community_comment_added", {
        post_id: postId,
        has_mentions: mentionedUserIds.length > 0,
        mention_count: mentionedUserIds.length,
        content_length: newComment.length,
      });

      onComment?.(postId, newComment, mentionedUserIds);

      // Clear the editor
      const editor = mainCommentTextareaRef.current?.editor;
      if (editor) {
        editor.commands.setContent("");
      }
      setNewComment("");
    }
  };

  const handleReaction = useCallback(
    async (commentId: string, emoji: string) => {
      if (!user?.id) return;

      const currentReactions = commentReactions[commentId] || [];
      const existingReaction = currentReactions.find((r) => r.userId === user.id);
      const isRemoving = existingReaction && existingReaction.emoji === emoji;

      // Optimistic update
      setCommentReactions((prev) => {
        const reactions = prev[commentId] || [];
        let newReactions: Array<{ userId: string; userName: string; emoji: string }>;

        if (isRemoving) {
          // Same emoji - remove the reaction
          newReactions = reactions.filter((r) => r.userId !== user.id);
        } else {
          // Different emoji or new reaction
          newReactions = reactions.filter((r) => r.userId !== user.id);
          newReactions.push({ userId: user.id, userName: user.full_name || "You", emoji });
        }

        return {
          ...prev,
          [commentId]: newReactions,
        };
      });

      // Track comment reaction event
      trackEvent("community_comment_reaction", {
        post_id: postId,
        comment_id: commentId,
        emoji,
        action: isRemoving ? "remove" : "add",
      });

      // Call API
      try {
        if (isRemoving) {
          // Remove reaction
          await removeReactComment(commentId);
        } else {
          // Add/update reaction
          await reactComment(commentId, { emoji });
        }
      } catch (error) {
        console.error("Error updating comment reaction:", error);
        // Revert on error by refetching
        try {
          const result = await getCommentReactions(commentId, 1, 50);
          const transformedReactions = result.reactions.map((reaction: ReactionUser) => ({
            userId: reaction.user.id,
            userName: reaction.user.full_name,
            emoji: reaction.emoji,
          }));
          setCommentReactions((prev) => ({
            ...prev,
            [commentId]: transformedReactions,
          }));
        } catch (fetchError) {
          console.error("Error fetching comment reactions:", fetchError);
        }
      }
    },
    [user?.id, commentReactions, postId, trackEvent],
  );

  // Auto-expand comment when autoExpandCommentId is provided (for navigation from notifications)
  useEffect(() => {
    if (autoExpandCommentId) {
      // Always expand the comment, even if it was previously collapsed
      const expandComment = async () => {
        setExpandedComments((prev) => new Set(prev).add(autoExpandCommentId));

        if (!fetchedReplies[autoExpandCommentId]) {
          setLoadingReplies((prev) => new Set(prev).add(autoExpandCommentId));

          try {
            const repliesData = await getRepliesByComment(autoExpandCommentId, 1, 10);

            // Transform API replies to UI format
            const transformedReplies = repliesData.replies.map((reply: any) => ({
              id: reply.id || reply._id,
              author: {
                id: reply.author?.id || reply.author_id,
                name: reply.author?.full_name || t("community.comments.unknown_user"),
                avatar: reply.author?.photo_thumbnail_url || reply.author?.photo,
                title:
                  reply.author?.job_title ||
                  (reply.author?.type === "employer" ? t("employer") : t("job_seeker")),
                type: reply.author?.type,
                organizationId: reply.author?.organization_id,
              },
              content: reply.content,
              timestamp: new Date(reply.created_at),
              isEdited: reply.updated_at !== reply.created_at,
              editedAt:
                reply.updated_at !== reply.created_at ? new Date(reply.updated_at) : undefined,
              likes: [],
            }));

            setFetchedReplies((prev) => ({
              ...prev,
              [autoExpandCommentId]: transformedReplies,
            }));

            setRepliesPage((prev) => ({
              ...prev,
              [autoExpandCommentId]: 1,
            }));

            setHasMoreReplies((prev) => ({
              ...prev,
              [autoExpandCommentId]: repliesData.pagination.page < repliesData.pagination.pages,
            }));

            // Fetch reactions for each reply (only if not already fetched)
            transformedReplies.forEach(async (reply: any) => {
              // Skip if already fetched
              if (fetchedReactionsRef.current.has(reply.id)) {
                return;
              }

              try {
                const result = await getCommentReactions(reply.id, 1, 50);
                const transformedReactions = result.reactions.map((reaction: ReactionUser) => ({
                  userId: reaction.user.id,
                  userName: reaction.user.full_name,
                  emoji: reaction.emoji,
                }));

                // Mark as fetched
                fetchedReactionsRef.current.add(reply.id);

                if (transformedReactions.length > 0) {
                  setCommentReactions((prev) => ({
                    ...prev,
                    [reply.id]: transformedReactions,
                  }));
                }
              } catch (error) {
                // Silently fail if no reactions
                console.debug(`No reactions for reply ${reply.id}`);
              }
            });
          } catch (error) {
            console.error("Error fetching replies:", error);
          } finally {
            setLoadingReplies((prev) => {
              const newSet = new Set(prev);
              newSet.delete(autoExpandCommentId);
              return newSet;
            });
          }
        }
      };

      expandComment();
    }
  }, [autoExpandCommentId]); // Only depend on autoExpandCommentId to avoid infinite loops

  // Scroll to and highlight specific comment or reply after DOM updates
  useEffect(() => {
    if (!autoExpandCommentId) return;

    // Create a unique key for this highlight combination
    const highlightKey = `${autoExpandCommentId}-${highlightReplyId || "comment"}`;

    // Skip if we've already highlighted this combination
    if (hasHighlightedRef.current === highlightKey) return;

    // Wait for the DOM to update and replies to be rendered
    const timer = setTimeout(() => {
      let targetElement: HTMLElement | null = null;

      if (highlightReplyId) {
        // Scroll to specific reply
        targetElement = document.getElementById(`reply-${highlightReplyId}`);
      } else {
        // Scroll to specific comment
        targetElement = document.getElementById(`comment-${autoExpandCommentId}`);
      }

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });

        // Add light blue highlight effect
        targetElement.classList.add("ring-2", "ring-primary", "bg-brand-accent", "rounded-2xl");
        targetElement.style.padding = "12px";
        targetElement.style.transition = "all 0.3s ease";
        targetElement.style.transform = "scale(1.02)";

        // Pulse animation
        targetElement.animate(
          [
            { transform: "scale(1.02)", opacity: 1 },
            { transform: "scale(1.04)", opacity: 0.95 },
            { transform: "scale(1.02)", opacity: 1 },
          ],
          {
            duration: 500,
            iterations: 2,
          },
        );

        // Remove highlight after 4 seconds
        setTimeout(() => {
          if (targetElement) {
            targetElement.classList.remove(
              "ring-2",
              "ring-primary",
              "bg-brand-accent",
              "rounded-2xl",
            );
            targetElement.style.transform = "scale(1)";
            targetElement.style.padding = "0";
          }
        }, 4000);

        // Mark this combination as highlighted
        hasHighlightedRef.current = highlightKey;
      }
    }, 800); // Wait longer for replies to be fetched and rendered

    return () => clearTimeout(timer);
  }, [autoExpandCommentId, highlightReplyId, fetchedReplies]);

  const toggleReplies = useCallback(
    async (commentId: string) => {
      // Validate commentId
      if (!commentId) {
        console.error("toggleReplies: commentId is undefined or empty");
        return;
      }

      const isExpanding = !expandedComments.has(commentId);

      if (isExpanding) {
        trackEvent("community_replies_expanded", {
          post_id: postId,
          comment_id: commentId,
        });
      }

      setExpandedComments((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(commentId)) {
          newSet.delete(commentId);
        } else {
          newSet.add(commentId);
        }
        return newSet;
      });

      // Fetch replies if expanding and not already fetched
      if (isExpanding && !fetchedReplies[commentId]) {
        setLoadingReplies((prev) => new Set(prev).add(commentId));

        try {
          const repliesData = await getRepliesByComment(commentId, 1, 10);

          // Transform API replies to UI format
          const transformedReplies = repliesData.replies.map((reply: any) => ({
            id: reply.id || reply._id,
            author: {
              id: reply.author?.id || reply.author_id,
              name: reply.author?.full_name || t("community.comments.unknown_user"),
              avatar: reply.author?.photo_thumbnail_url || reply.author?.photo,
              title:
                reply.author?.job_title ||
                (reply.author?.type === "employer" ? t("employer") : t("job_seeker")),
              type: reply.author?.type,
              organizationId: reply.author?.organization_id,
            },
            content: reply.content,
            timestamp: new Date(reply.created_at),
            isEdited: reply.updated_at !== reply.created_at,
            editedAt:
              reply.updated_at !== reply.created_at ? new Date(reply.updated_at) : undefined,
            likes: [],
          }));

          setFetchedReplies((prev) => ({
            ...prev,
            [commentId]: transformedReplies,
          }));

          // Fetch reactions for each reply (only if not already fetched)
          transformedReplies.forEach(async (reply: any) => {
            // Skip if already fetched
            if (fetchedReactionsRef.current.has(reply.id)) {
              return;
            }

            try {
              const result = await getCommentReactions(reply.id, 1, 50);
              const transformedReactions = result.reactions.map((reaction: ReactionUser) => ({
                userId: reaction.user.id,
                userName: reaction.user.full_name,
                emoji: reaction.emoji,
              }));

              // Mark as fetched
              fetchedReactionsRef.current.add(reply.id);

              if (transformedReactions.length > 0) {
                setCommentReactions((prev) => ({
                  ...prev,
                  [reply.id]: transformedReactions,
                }));
              }
            } catch (error) {
              // Silently fail if no reactions
              console.debug(`No reactions for reply ${reply.id}`);
              // Still mark as fetched
              fetchedReactionsRef.current.add(reply.id);
            }
          });

          // Track pagination
          setRepliesPage((prev) => ({ ...prev, [commentId]: 1 }));
          setHasMoreReplies((prev) => ({
            ...prev,
            [commentId]: repliesData.pagination.page < repliesData.pagination.pages,
          }));
        } catch (error) {
          console.error("Error fetching replies:", error);
          toast({
            title: t("community.comments.failed_to_load_replies"),
            description: t("community.comments.failed_to_load_replies_desc"),
            variant: "destructive",
          });
        } finally {
          setLoadingReplies((prev) => {
            const newSet = new Set(prev);
            newSet.delete(commentId);
            return newSet;
          });
        }
      }
    },
    [expandedComments, fetchedReplies, toast, postId, trackEvent],
  );

  // Function to load more replies for a comment
  const loadMoreReplies = useCallback(
    async (commentId: string) => {
      if (!hasMoreReplies[commentId] || loadingMoreReplies.has(commentId)) return;

      setLoadingMoreReplies((prev) => new Set(prev).add(commentId));

      try {
        const nextPage = (repliesPage[commentId] || 1) + 1;
        const repliesData = await getRepliesByComment(commentId, nextPage, 10);

        // Transform API replies to UI format
        const transformedReplies = repliesData.replies.map((reply: any) => ({
          id: reply.id || reply._id,
          author: {
            id: reply.author?.id || reply.author_id,
            name: reply.author?.full_name || t("community.comments.unknown_user"),
            avatar: reply.author?.photo_thumbnail_url || reply.author?.photo,
            title:
              reply.author?.job_title ||
              (reply.author.type === "employer" ? t("employer") : t("job_seeker")),
          },
          content: reply.content,
          timestamp: new Date(reply.created_at),
          isEdited: reply.updated_at !== reply.created_at,
          editedAt: reply.updated_at !== reply.created_at ? new Date(reply.updated_at) : undefined,
          likes: [],
        }));

        // Append new replies to existing ones
        setFetchedReplies((prev) => ({
          ...prev,
          [commentId]: [...(prev[commentId] || []), ...transformedReplies],
        }));

        // Fetch reactions for each new reply (only if not already fetched)
        transformedReplies.forEach(async (reply: any) => {
          // Skip if already fetched
          if (fetchedReactionsRef.current.has(reply.id)) {
            return;
          }

          try {
            const result = await getCommentReactions(reply.id, 1, 50);
            const transformedReactions = result.reactions.map((reaction: ReactionUser) => ({
              userId: reaction.user.id,
              userName: reaction.user.full_name,
              emoji: reaction.emoji,
            }));

            // Mark as fetched
            fetchedReactionsRef.current.add(reply.id);

            if (transformedReactions.length > 0) {
              setCommentReactions((prev) => ({
                ...prev,
                [reply.id]: transformedReactions,
              }));
            }
          } catch (error) {
            // Silently fail if no reactions
            console.debug(`No reactions for reply ${reply.id}`);
            // Still mark as fetched
            fetchedReactionsRef.current.add(reply.id);
          }
        });

        // Update pagination state
        setRepliesPage((prev) => ({ ...prev, [commentId]: nextPage }));
        setHasMoreReplies((prev) => ({
          ...prev,
          [commentId]: repliesData.pagination.page < repliesData.pagination.pages,
        }));
      } catch (error) {
        console.error("Error loading more replies:", error);
        toast({
          title: t("community.comments.failed_to_load_more"),
          description: t("community.comments.failed_to_load_more_desc"),
          variant: "destructive",
        });
      } finally {
        setLoadingMoreReplies((prev) => {
          const newSet = new Set(prev);
          newSet.delete(commentId);
          return newSet;
        });
      }
    },
    [hasMoreReplies, loadingMoreReplies, repliesPage, toast],
  );

  const CommentItem = ({
    comment,
    isReply = false,
    parentCommentId,
  }: {
    comment: any;
    isReply?: boolean;
    parentCommentId?: string;
  }) => {
    // Use shared state from parent to persist reply box state across re-renders
    const showReplyBox = openReplyBoxes.has(comment.id);
    const setShowReplyBox = (open: boolean) => {
      setOpenReplyBoxes((prev) => {
        const newSet = new Set(prev);
        if (open) {
          newSet.add(comment.id);
        } else {
          newSet.delete(comment.id);
          // Clean up reply content when closing reply box
          delete replyContentsRef.current[comment.id];
        }
        return newSet;
      });
    };

    // Use shared ref from parent to persist reply content across re-renders (ref avoids re-renders on typing)
    const setLocalReplyContent = (content: string) => {
      replyContentsRef.current[comment.id] = content;
    };
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [isEditingComment, setIsEditingComment] = useState(false);
    const [editCommentContent, setEditCommentContent] = useState(comment.content);
    const [showEditEmojiPicker, setShowEditEmojiPicker] = useState(false);
    const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [reportTarget, setReportTarget] = useState<{ id: string; type: ReportTargetType } | null>(
      null,
    );
    const [reactionUsers, setReactionUsers] = useState<ReactionUser[]>([]);
    const [isLoadingReactions, setIsLoadingReactions] = useState(false);
    const [showReactionUsersDialog, setShowReactionUsersDialog] = useState(false);
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);
    const editCommentTextareaRef = useRef<TiptapRef>(null);
    const replyTextareaRef = useRef<TiptapRef>(null);

    // Auto-focus reply textarea and set default mention when reply box is opened
    useEffect(() => {
      if (showReplyBox && replyTextareaRef.current?.editor) {
        // Use setTimeout to ensure the component is fully rendered
        setTimeout(() => {
          const editor = replyTextareaRef.current?.editor;
          if (editor) {
            // Only set mention if editor is empty and we haven't initialized yet
            const isEmpty = editor.isEmpty;
            const hasInitialized = initializedMentionsRef.current.has(comment.id);

            if (isEmpty && !hasInitialized) {
              // Create mention HTML with all necessary data attributes
              const mentionHTML = `<span class="mention text-primary font-semibold" data-type="mention" data-id="${comment.author.id}" data-label="${comment.author.name}"${comment.author.type ? ` data-user-type="${comment.author.type}"` : ""}${comment.author.organizationId ? ` data-organization-id="${comment.author.organizationId}"` : ""}>@${comment.author.name}</span>&nbsp;`;
              editor.commands.setContent(mentionHTML);
              initializedMentionsRef.current.add(comment.id);
            }
            editor.commands.focus("end");
          }
        }, 100);
      } else if (!showReplyBox) {
        // Reset flag when reply box is closed
        initializedMentionsRef.current.delete(comment.id);
      }
    }, [showReplyBox]);

    const commentLikes = useMemo(() => {
      return commentReactions[comment.id] || comment.likes || [];
    }, [commentReactions[comment.id], comment.likes]);

    const groupedReactions = useMemo(() => {
      return commentLikes.reduce(
        (acc: Record<string, number>, like: any) => {
          acc[like.emoji] = (acc[like.emoji] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
    }, [commentLikes]);

    // Use fetched replies if available, otherwise use comment.replies
    const replies = fetchedReplies[comment.id] || comment.replies || [];
    const replyCount = comment.replies_count || comment.replies?.length || 0;
    const isExpanded = expandedComments.has(comment.id);
    const isLoadingRepliesForComment = loadingReplies.has(comment.id);

    // Find user's reaction to this comment
    const userReaction = useMemo(() => {
      return commentLikes.find((like: any) => like.userId === user?.id);
    }, [commentLikes, user?.id]);

    const userReactionEmoji = useMemo(() => {
      return userReaction ? reactions.find((r) => r.emoji === userReaction.emoji) : null;
    }, [userReaction]);

    // Check if current user is the author of this comment
    const isOwnComment = user?.id && comment.author.id === user.id;

    // User can edit their own comment only
    const canEdit = isOwnComment;

    // User can only delete their own comments
    const canDelete = isOwnComment;

    const handleOpenReactionUsersDialog = async () => {
      setShowReactionUsersDialog(true);

      // Only fetch if we haven't fetched yet or if we need to refresh
      if (reactionUsers.length === 0 && commentLikes.length > 0) {
        setIsLoadingReactions(true);
        try {
          const result = await getCommentReactions(comment.id, 1, 50);
          setReactionUsers(result.reactions as ReactionUser[]);
        } catch (error) {
          console.error("Error fetching reactions:", error);
        } finally {
          setIsLoadingReactions(false);
        }
      }
    };

    const addEmojiToEdit = (emoji: string) => {
      const editor = editCommentTextareaRef.current?.editor;
      if (editor) {
        editor.commands.insertContent(emoji);
      }
      setShowEditEmojiPicker(false);
    };

    const addEmojiToReply = (emoji: string) => {
      const editor = replyTextareaRef.current?.editor;
      if (editor) {
        editor.commands.insertContent(emoji);
      }
      setShowReplyEmojiPicker(false);
    };

    const handleSaveEdit = async () => {
      if (editCommentContent.trim() && editCommentContent !== comment.content) {
        // Extract mentioned user IDs from the HTML
        const mentionedUserIds = extractMentionedUserIds(editCommentContent);

        if (isReply && parentCommentId && comment.id) {
          // Editing a reply
          onEditReply?.(postId, parentCommentId, comment.id, editCommentContent, mentionedUserIds);

          // Refetch replies to show the edit
          if (parentCommentId) {
            try {
              const repliesData = await getRepliesByComment(parentCommentId, 1, 10);
              const transformedReplies = repliesData.replies.map((reply: any) => ({
                id: reply.id || reply._id,
                author: {
                  id: reply.author?.id || reply.author_id,
                  name: reply.author?.full_name || t("community.comments.unknown_user"),
                  avatar: reply.author?.photo_thumbnail_url || reply.author?.photo,
                  title:
                    reply.author?.job_title ||
                    (reply.author.type === "employer" ? t("employer") : t("job_seeker")),
                },
                content: reply.content,
                timestamp: new Date(reply.created_at),
                isEdited: reply.updated_at !== reply.created_at,
                editedAt:
                  reply.updated_at !== reply.created_at ? new Date(reply.updated_at) : undefined,
                likes: [],
              }));

              setFetchedReplies((prev) => ({
                ...prev,
                [parentCommentId]: transformedReplies,
              }));
            } catch (error) {
              console.error("Error refetching replies:", error);
            }
          }
        } else if (comment.id) {
          // Editing a comment
          onEditComment?.(postId, comment.id, editCommentContent, mentionedUserIds);
        }
        setIsEditingComment(false);
      }
    };

    const handleCancelEdit = () => {
      setEditCommentContent(comment.content);
      setIsEditingComment(false);
    };

    const handleDeleteComment = async () => {
      if (isReply && parentCommentId && comment.id) {
        // Immediately remove the reply from local state for instant UI update
        setFetchedReplies((prev) => {
          const parentReplies = prev[parentCommentId] || [];
          const updatedReplies = parentReplies.filter((reply) => reply.id !== comment.id);
          return {
            ...prev,
            [parentCommentId]: updatedReplies,
          };
        });

        // Close dialog immediately
        setShowDeleteDialog(false);

        // Call API to delete (in background)
        onDeleteReply?.(postId, parentCommentId, comment.id);

        toast({
          title: t("community.comments.reply_deleted"),
          description: t("community.comments.reply_deleted_desc"),
        });
      } else if (comment.id) {
        // Close dialog immediately
        setShowDeleteDialog(false);

        // Call API to delete comment
        onDeleteComment?.(postId, comment.id);

        toast({
          title: t("community.comments.comment_deleted"),
          description: t("community.comments.comment_deleted_desc"),
        });
      }
    };

    const handleAddReply = async () => {
      // Check if user is suspended
      if (checkAndNotify()) {
        // Clear reply input
        const editor = replyTextareaRef.current?.editor;
        if (editor) {
          editor.commands.setContent("");
        }
        setLocalReplyContent("");
        setShowReplyBox(false);
        return;
      }

      const currentReplyContent = replyContentsRef.current[comment.id] || "";
      if (currentReplyContent.trim() && comment.id) {
        const mentionedUserIds = extractMentionedUserIds(currentReplyContent);
        const replyContent = currentReplyContent;

        // Always include the comment author in mentioned users when replying
        const allMentionedUserIds = comment.author.id
          ? Array.from(new Set([comment.author.id, ...mentionedUserIds]))
          : mentionedUserIds;

        // Set submitting state
        setIsSubmittingReply(true);

        // Clear input
        const editor = replyTextareaRef.current?.editor;
        if (editor) {
          editor.commands.setContent("");
        }
        setLocalReplyContent("");

        try {
          // Call API to create reply
          const newReply = await createComment({
            post_id: postId,
            parent_comment_id: comment.id,
            content: replyContent,
            mentioned_users: allMentionedUserIds,
          });

          // Transform the API response to UI format
          const transformedReply = {
            id: newReply.id || newReply._id,
            author: {
              id: newReply.author?.id || newReply.author_id,
              name: newReply.author?.full_name || t("community.comments.unknown_user"),
              avatar: newReply.author?.photo_thumbnail_url || newReply.author?.photo,
              title:
                newReply.author?.job_title ||
                (newReply.author?.type === "employer" ? t("employer") : t("job_seeker")),
              type: newReply.author?.type,
              organizationId: newReply.author?.organization_id,
            },
            content: newReply.content,
            timestamp: new Date(newReply.created_at),
            isEdited: false,
            likes: [],
          };

          // Add the new reply to existing replies immediately
          setFetchedReplies((prev) => {
            const currentReplies = prev[comment.id] || [];
            return {
              ...prev,
              [comment.id]: [...currentReplies, transformedReply],
            };
          });

          // Expand the comment to show the new reply
          if (!expandedComments.has(comment.id)) {
            setExpandedComments((prev) => new Set(prev).add(comment.id));
          }
          setShowReplyBox(false);
          // Call parent handler for any additional state updates
          onAddReply?.(postId, comment.id, replyContent, allMentionedUserIds);

          // Close reply box after successful submission

          toast({
            title: t("community.comments.reply_added"),
            description: t("community.comments.reply_added_desc"),
          });
        } catch (error) {
          console.error("Error adding reply:", error);
          toast({
            title: t("community.comments.failed_to_add_reply"),
            description: t("community.comments.failed_to_add_reply_desc"),
            variant: "destructive",
          });
        } finally {
          setIsSubmittingReply(false);
        }
      }
    };

    return (
      <div className="mb-4">
        <div id={isReply ? `reply-${comment.id}` : `comment-${comment.id}`} className="flex gap-3">
          <Avatar
            className="ring-border hover:ring-primary/50 h-10 w-10 cursor-pointer ring-2 transition"
            onClick={() =>
              handleUserProfileClick(
                comment.author.id,
                comment.author.type,
                comment.author.organizationId,
              )
            }
          >
            <AvatarImage src={comment.author.avatar} />
            <AvatarFallback className="bg-brand-accent text-primary text-xs font-medium">
              {comment.author.name
                .split(" ")
                .map((n: string) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            {isEditingComment ? (
              <div className="space-y-3">
                <Tiptap
                  ref={editCommentTextareaRef}
                  content={editCommentContent}
                  onChange={setEditCommentContent}
                  placeholder={t("community.comments.edit_comment")}
                  enableMention={true}
                  enableLink={false}
                />

                <div className="flex justify-between gap-2">
                  <Popover open={showEditEmojiPicker} onOpenChange={setShowEditEmojiPicker}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:bg-primary/10 hover:text-primary gap-2"
                      >
                        <Smile className="h-3 w-3" />
                        <span className="text-xs">{t("community.comments.emoji_button")}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                            🏗️ {t("community.create_post.construction_emojis")}
                          </h4>
                          <div className="grid grid-cols-8 gap-1">
                            {constructionEmojis.map((item, index) => (
                              <button
                                key={index}
                                onClick={() => addEmojiToEdit(item.emoji)}
                                className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                                type="button"
                              >
                                {item.emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                        <Separator />
                        <div>
                          <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                            😊 {t("community.create_post.common")}
                          </h4>
                          <div className="grid grid-cols-8 gap-1">
                            {commonEmojis.map((item, index) => (
                              <button
                                key={index}
                                onClick={() => addEmojiToEdit(item.emoji)}
                                className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                                type="button"
                              >
                                {item.emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} size="sm" className="gap-2">
                      <Check className="h-3 w-3" />
                      {t("community.comments.save")}
                    </Button>
                    <Button onClick={handleCancelEdit} size="sm" variant="ghost" className="gap-2">
                      <X className="h-3 w-3" />
                      {t("community.comments.cancel")}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-muted/50 hover:bg-muted/70 rounded-2xl px-4 py-3 transition-colors">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="cursor-pointer text-sm font-semibold hover:underline"
                      onClick={() =>
                        handleUserProfileClick(
                          comment.author.id,
                          comment.author.type,
                          comment.author.organizationId,
                        )
                      }
                    >
                      {comment.author.name}
                    </span>
                    <span className="text-muted-foreground text-xs">{comment.author.title}</span>
                    {comment.isEdited && (
                      <>
                        <span className="text-muted-foreground text-xs">•</span>
                        <span className="text-muted-foreground text-xs italic">
                          {t("community.comments.edited")}
                        </span>
                      </>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="hover:bg-muted h-6 w-6 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="border-border rounded-xl p-1.5 shadow-lg"
                    >
                      {canEdit && (
                        <DropdownMenuItem
                          onClick={() => setIsEditingComment(true)}
                          className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
                        >
                          <Edit2 className="h-3 w-3" />
                          {t("community.comments.edit")}
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          onClick={() => setShowDeleteDialog(true)}
                          className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
                        >
                          <Trash2 className="h-3 w-3" />
                          {t("community.comments.delete")}
                        </DropdownMenuItem>
                      )}
                      {!isOwnComment && (
                        <DropdownMenuItem
                          onClick={() => {
                            setReportTarget({
                              id: comment.id,
                              type: isReply ? "reply" : "comment",
                            });
                            setShowReportDialog(true);
                          }}
                          className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-amber-700 focus:bg-amber-50 focus:text-amber-700"
                        >
                          <Flag className="h-3 w-3" />
                          {t("community.report.report")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <MentionRenderer content={comment.content} className="text-sm" />
              </div>
            )}

            {/* Comment Actions */}
            <div className="mt-2 ml-4 flex items-center gap-4">
              <Popover open={showReactionPicker} onOpenChange={setShowReactionPicker}>
                <PopoverTrigger asChild>
                  <button
                    className={`flex items-center gap-1 text-xs font-semibold transition-colors ${userReactionEmoji ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
                  >
                    {userReactionEmoji ? (
                      <>
                        <span className="text-sm">{userReactionEmoji.emoji}</span>
                        <span>{userReactionEmoji.label}</span>
                      </>
                    ) : (
                      t("community.comments.like")
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto rounded-full p-1.5 shadow-lg" align="start">
                  <div className="flex items-center gap-0.5">
                    {reactions.map((reaction, index) => {
                      const isUserReaction = userReactionEmoji?.emoji === reaction.emoji;
                      return (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                handleReaction(comment.id, reaction.emoji);
                                setShowReactionPicker(false);
                              }}
                              className={`flex h-10 w-10 items-center justify-center rounded-full text-2xl transition-transform duration-150 hover:-translate-y-1 hover:scale-125 active:scale-95 ${
                                isUserReaction
                                  ? "bg-brand-accent ring-primary ring-2"
                                  : "hover:bg-accent"
                              }`}
                              type="button"
                            >
                              {reaction.emoji}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {reaction.label}
                              {isUserReaction ? ` ${t("community.comments.selected")}` : ""}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>

              {!isReply && (
                <button
                  onClick={() => {
                    if (!showReplyBox) {
                      // Open reply box first
                      setShowReplyBox(true);

                      // Pre-populate with proper mention tag HTML (same format as manual mentions)
                      setTimeout(() => {
                        const editor = replyTextareaRef.current?.editor;
                        if (editor) {
                          // Create mention HTML with all necessary data attributes
                          const mentionHTML = `<span class="mention text-primary font-semibold" data-type="mention" data-id="${comment.author.id}" data-label="${comment.author.name}"${comment.author.type ? ` data-user-type="${comment.author.type}"` : ""}${comment.author.organizationId ? ` data-organization-id="${comment.author.organizationId}"` : ""}>@${comment.author.name}</span>&nbsp;`;

                          editor.commands.setContent(mentionHTML);
                          editor.commands.focus("end");
                        }
                      }, 100);
                    } else {
                      // If reply box is already open, just focus the editor
                      const editor = replyTextareaRef.current?.editor;
                      if (editor) {
                        editor.commands.focus("end");
                      }
                    }
                  }}
                  disabled={isSubmittingReply}
                  className="text-muted-foreground hover:text-primary text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("community.comments.reply")}
                </button>
              )}

              <span className="text-muted-foreground text-xs">
                {formatTimestamp(comment.timestamp)}
              </span>

              {Object.keys(groupedReactions).length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleOpenReactionUsersDialog}
                      className="group/creact border-border bg-muted/60 hover:border-brand-accent-border ml-auto inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 transition-colors hover:bg-blue-50"
                    >
                      <span className="flex -space-x-1">
                        {Object.keys(groupedReactions)
                          .slice(0, 3)
                          .map((emoji, index) => (
                            <span
                              key={index}
                              className="bg-card ring-border flex h-4 w-4 items-center justify-center rounded-full text-[11px] ring-1"
                            >
                              {emoji}
                            </span>
                          ))}
                      </span>
                      <span className="text-muted-foreground group-hover/creact:text-primary text-[11px] font-semibold">
                        {(Object.values(groupedReactions) as number[]).reduce(
                          (sum, count) => sum + count,
                          0,
                        )}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-sm">
                      {commentLikes.map((like: any) => like.userName).join(", ")}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Reply Count Button - Only for parent comments with replies */}
            {!isReply && replyCount > 0 && (
              <button
                onClick={() => toggleReplies(comment.id)}
                className="text-primary hover:text-primary/80 group mt-3 ml-4 flex items-center gap-2 text-sm font-semibold transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 transition-transform group-hover:translate-y-[-2px]" />
                ) : (
                  <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-[2px]" />
                )}
                <MessageCircle className="h-4 w-4" />
                <span>
                  {replyCount}{" "}
                  {replyCount === 1
                    ? t("community.comments.reply_singular")
                    : t("community.comments.replies")}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Nested Replies - Expandable/Collapsible */}
        {!isReply && isExpanded && (
          <div className="border-border mt-4 ml-5 space-y-4 border-l-2 pl-4">
            {isLoadingRepliesForComment ? (
              <div className="ml-4 flex items-center justify-center gap-2 py-4">
                <Loader2 className="text-primary h-4 w-4 animate-spin" />
                <span className="text-muted-foreground text-sm">
                  {t("community.comments.loading_replies")}
                </span>
              </div>
            ) : replies.length > 0 ? (
              <>
                {replies.map((reply: any) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    isReply={true}
                    parentCommentId={comment.id as string}
                  />
                ))}

                {/* Show More Replies Button */}
                {hasMoreReplies[comment.id] && (
                  <div className="ml-4 flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => loadMoreReplies(comment.id)}
                      disabled={loadingMoreReplies.has(comment.id)}
                      className="text-muted-foreground hover:text-foreground text-xs"
                    >
                      {loadingMoreReplies.has(comment.id) ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          {t("community.comments.loading_replies")}
                        </>
                      ) : (
                        <>{t("community.comments.show_more_replies")}</>
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : replyCount > 0 ? (
              <div className="ml-4 py-4 text-center">
                <p className="text-muted-foreground text-sm">
                  {t("community.comments.no_replies")}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {/* Reply Box - At the bottom after all replies */}
        {showReplyBox && !isReply && (
          <div
            key={`reply-box-${comment.id}`}
            className="bg-muted/30 mt-4 ml-4 flex gap-3 rounded-lg border border-dashed p-4"
          >
            <Avatar className="ring-border h-10 w-10 ring-2">
              <AvatarImage src={user?.photo_thumbnail_url || user?.photo || ""} />
              <AvatarFallback className="bg-brand-accent text-primary text-xs font-medium">
                {getUserInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-3">
              <Tiptap
                key={`reply-editor-${comment.id}`}
                ref={replyTextareaRef}
                content={replyContentsRef.current[comment.id] || ""}
                onChange={setLocalReplyContent}
                placeholder={t("community.comments.write_reply")}
                enableMention={true}
                enableLink={false}
              />
              <div className="flex justify-between gap-2">
                <Popover open={showReplyEmojiPicker} onOpenChange={setShowReplyEmojiPicker}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="hover:bg-primary/10 hover:text-primary gap-2"
                    >
                      <Smile className="h-3 w-3" />
                      <span className="text-xs">{t("community.comments.emoji_button")}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                          🏗️ {t("community.create_post.construction_emojis")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {constructionEmojis.map((item, index) => (
                            <button
                              key={index}
                              onClick={() => addEmojiToReply(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                              type="button"
                            >
                              {item.emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                          😊 {t("community.create_post.common")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {commonEmojis.map((item, index) => (
                            <button
                              key={index}
                              onClick={() => addEmojiToReply(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                              type="button"
                            >
                              {item.emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddReply} disabled={isSubmittingReply}>
                    {t("community.comments.reply")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowReplyBox(false);
                      setLocalReplyContent("");
                    }}
                  >
                    {t("community.comments.cancel")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isReply
                  ? t("community.comments.delete_reply_title")
                  : t("community.comments.delete_comment_title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isReply
                  ? t("community.comments.delete_reply_desc")
                  : t("community.comments.delete_comment_desc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("community.comments.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteComment}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("community.comments.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reaction Users Dialog */}
        <Dialog open={showReactionUsersDialog} onOpenChange={setShowReactionUsersDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("community.comments.reactions")}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {isLoadingReactions ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="flex items-center gap-3 p-2">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-[60%]" />
                        <Skeleton className="h-3 w-[40%]" />
                      </div>
                      <Skeleton className="h-6 w-6" />
                    </div>
                  ))}
                </div>
              ) : reactionUsers?.length && reactionUsers?.length > 0 ? (
                <div className="max-h-[400px] space-y-3 overflow-y-auto">
                  {reactionUsers?.map((reaction, index) => (
                    <div
                      key={index}
                      className="hover:bg-accent flex items-center gap-3 rounded-lg p-2 transition-colors"
                    >
                      <Avatar
                        className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80"
                        onClick={() =>
                          reaction?.user?.id &&
                          reaction?.user?.type &&
                          handleUserProfileClick(
                            reaction.user.id,
                            reaction.user.type,
                            reaction.user.organization_id,
                          )
                        }
                      >
                        <AvatarImage
                          src={reaction?.user?.photo_thumbnail_url}
                          alt={reaction?.user?.full_name}
                        />
                        <AvatarFallback>{reaction?.user?.full_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p
                          className="cursor-pointer truncate text-sm font-medium hover:underline"
                          onClick={() =>
                            reaction?.user?.id &&
                            reaction?.user?.type &&
                            handleUserProfileClick(
                              reaction.user.id,
                              reaction.user.type,
                              reaction.user.organization_id,
                            )
                          }
                        >
                          {reaction?.user?.full_name}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {reaction?.user?.job_title ||
                            (reaction?.user?.type === "job_seeker"
                              ? t("job_seeker")
                              : t("employer"))}
                        </p>
                      </div>
                      <span className="text-lg">{reaction?.emoji}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  {t("community.comments.no_reactions")}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        {reportTarget && (
          <ReportDialog
            open={showReportDialog}
            onOpenChange={(open) => {
              setShowReportDialog(open);
              if (!open) setReportTarget(null);
            }}
            targetId={reportTarget.id}
            targetType={reportTarget.type}
          />
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 pt-2">
        {/* Comments List */}
        {comments.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageCircle className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-sm font-semibold">
                {comments.length}{" "}
                {comments.length === 1
                  ? t("community.comments.comment")
                  : t("community.comments.comments")}
              </span>
            </div>
            {comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        )}

        {/* Show More Button */}
        {showMoreButton}

        {/* Add Comment */}
        <div className="bg-muted/20 flex gap-3 rounded-lg border p-4">
          <Avatar className="ring-border h-10 w-10 ring-2">
            <AvatarImage src={user?.photo_thumbnail_url || user?.photo || ""} />
            <AvatarFallback className="bg-brand-accent text-primary text-xs font-medium">
              {getUserInitials(user?.full_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-3">
            <Tiptap
              ref={mainCommentTextareaRef}
              content={newComment}
              onChange={handleCommentChange}
              placeholder={t("community.comments.write_comment")}
              enableMention={true}
              enableLink={false}
            />
            <div className="flex items-center justify-between gap-2">
              <Popover
                open={showMainCommentEmojiPicker}
                onOpenChange={setShowMainCommentEmojiPicker}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-primary/10 hover:text-primary gap-2"
                  >
                    <Smile className="h-4 w-4" />
                    <span className="text-xs">{t("community.comments.emoji_button")}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                        🏗️ {t("community.create_post.construction_emojis")}
                      </h4>
                      <div className="grid grid-cols-8 gap-1">
                        {constructionEmojis.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => addEmojiToComment(item.emoji)}
                            className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                            type="button"
                          >
                            {item.emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                        😊 {t("community.create_post.common")}
                      </h4>
                      <div className="grid grid-cols-8 gap-1">
                        {commonEmojis.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => addEmojiToComment(item.emoji)}
                            className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                            type="button"
                          >
                            {item.emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={handleAddComment} size="sm" disabled={!newComment.trim()}>
                {t("community.comments.comment")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
